// v3.18-1334 / v3.18-1336 / v3.18-1339 — Call Assist slide-in panel.
//
// Shown to the right of the Call Outcome modal so the rep never has
// to leave the call to look up a script.  Renders every visible script
// grouped by category, with an instant-filter search, per-user
// last-used category memory, and (Director / Admin only) full script
// management: create, edit, archive, unarchive, duplicate, version
// history, and version restore — all without leaving the panel.
//
// Endpoints consumed:
//   GET   /api/call-assist/scripts?q=&category=&client_id=&include_archived=
//   POST  /api/call-assist/scripts
//   PUT   /api/call-assist/scripts/{id}
//   POST  /api/call-assist/scripts/{id}/archive
//   POST  /api/call-assist/scripts/{id}/unarchive
//   POST  /api/call-assist/scripts/{id}/duplicate
//   GET   /api/call-assist/scripts/{id}/versions
//   POST  /api/call-assist/scripts/{id}/restore/{version}
//   GET   /api/call-assist/last-category
//   PUT   /api/call-assist/last-category
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Search, X, Edit3, Save, RotateCcw, History,
         Bold, Italic, Underline, List, ListOrdered,
         Plus, Trash2, Copy, ArchiveRestore, Archive as ArchiveIcon,
         Eye, EyeOff } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const CATEGORY_LABELS = {
  opening:            'Opening',
  touched_base:       'Touched Base',
  follow_up:          'Follow Up',
  voicemail:          'Voicemail',
  objection_handling: 'Objection Handling',
  financing:          'Financing',
  loan_documents:     'Loan Documents',
  funding:            'Funding',
  collections:        'Collections',
  closing:            'Closing',
  custom:             'Company Scripts',
};

const SCOPE_LABELS = {
  global:   'Global',
  internal: 'Internal',
  company:  'Company Specific',
};

// v3.18-1336 — Compact rich-text toolbar for the inline script editor.
// The textarea remains the source of truth; toolbar buttons wrap the
// current selection in the requested HTML element.
const RichBtn = ({ children, onClick, tid, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className="inline-flex items-center justify-center h-6 w-6 border border-slate-200 rounded text-slate-600 hover:bg-slate-100"
    data-testid={tid}
  >
    {children}
  </button>
);

const _setNativeValue = (el, value) => {
  const proto = Object.getPrototypeOf(el);
  const desc  = Object.getOwnPropertyDescriptor(proto, 'value');
  if (desc && desc.set) desc.set.call(el, value); else el.value = value;
  el.dispatchEvent(new Event('input', { bubbles: true }));
};

const wrapSelection = (textareaId, tag) => {
  const ta = document.getElementById(textareaId);
  if (!ta) return;
  const start = ta.selectionStart;
  const end   = ta.selectionEnd;
  const selected = ta.value.slice(start, end) || 'text';
  const wrapped  = `<${tag}>${selected}</${tag}>`;
  const next = ta.value.slice(0, start) + wrapped + ta.value.slice(end);
  _setNativeValue(ta, next);
  ta.focus();
  ta.selectionStart = start + tag.length + 2;
  ta.selectionEnd   = start + tag.length + 2 + selected.length;
};

const wrapList = (textareaId, tag) => {
  const ta = document.getElementById(textareaId);
  if (!ta) return;
  const start = ta.selectionStart;
  const end   = ta.selectionEnd;
  const raw   = ta.value.slice(start, end);
  const lines = (raw || 'Item 1\nItem 2').split('\n').filter(Boolean);
  const inner = lines.map(l => `  <li>${l}</li>`).join('\n');
  const wrapped = `<${tag}>\n${inner}\n</${tag}>`;
  const next = ta.value.slice(0, start) + wrapped + ta.value.slice(end);
  _setNativeValue(ta, next);
  ta.focus();
};

const EMPTY_DRAFT = {
  id: null,
  title: '',
  body: '',
  category: 'opening',
  keywords: '',
  scope: 'global',
  company_id: '',
  active: true,
};

// v3.21-1630 · Intent detection + relevance scoring lived here in
// v3.21-1629.  Moved to backend/services/unified_ghost_service.py so
// every Ghost surface converges on ONE brain.  Do not re-add local
// scoring logic here — keep this component a thin client of
// POST /api/ghost/answer.
// v3.21-1630 · Ask Ghost inline widget — now a THIN CLIENT for the
// unified Ghost service.  All reasoning (STOPWORDS, semantic intent,
// weighted relevance, strong-match threshold, approved-content
// rejection, Suggested Wording fallback, source labeling) lives in
// backend/services/unified_ghost_service.py so every Ghost surface
// (Client Workspace, Call Assist, Global Ghost, Onboarding Modal)
// converges on ONE brain.  This component is now responsible only for
// the input, the loading state, and rendering the labeled response.
function AskGhostInline({ clientId, activeCat }) {
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState(null); // {source, title, body, intent}
  const ask = async () => {
    const term = (q || '').trim();
    if (!term) return;
    setBusy(true); setAnswer(null);
    try {
      const r = await api.post('/ghost/answer', {
        query:            term,
        capability:       'call_assist',
        active_client_id: clientId || undefined,
      });
      const d = r.data || {};
      setAnswer({
        source: d.source || 'suggested_wording',
        title:  d.title  || 'SUGGESTED WORDING',
        body:   d.body   || 'No response.',
        intent: d.intent || null,
      });
    } catch (e) {
      setAnswer({
        source: 'suggested_wording',
        title:  'SUGGESTED WORDING',
        body:   e?.response?.data?.detail || 'Ghost is unavailable right now.',
        intent: null,
      });
    } finally { setBusy(false); }
  };
  const label = (src) => {
    if (src === 'approved_script')       return 'Approved Script';
    if (src === 'operational_knowledge') return 'Approved Guidance';
    if (src === 'data')                  return 'Hub Data';
    if (src === 'query_help')            return 'Ghost Help';
    return 'Suggested Wording — review before using';
  };
  const labelClass = (src) => (
    src === 'approved_script'       ? 'text-emerald-700' :
    src === 'operational_knowledge' ? 'text-slate-700'   :
    src === 'data'                  ? 'text-blue-700'    :
    src === 'query_help'            ? 'text-slate-500'   :
                                       'text-indigo-700'
  );
  return (
    <div className="mt-2 rounded border border-indigo-200 bg-indigo-50/60 p-2" data-testid="call-assist-ask-ghost">
      <div className="flex items-center gap-1">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') ask(); }}
          placeholder="Ask Ghost — e.g. give me a way to say client declined"
          className="flex-1 text-sm rounded border border-indigo-200 px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-400"
          data-testid="call-assist-ask-ghost-input"
        />
        <button type="button" onClick={ask} disabled={busy || !q.trim()}
          className="text-xs font-semibold bg-indigo-600 text-white rounded px-2 py-1 hover:bg-indigo-500 disabled:opacity-50"
          data-testid="call-assist-ask-ghost-send">
          {busy ? '…' : 'Ask'}
        </button>
      </div>
      {answer && (
        <div className="mt-2 text-xs bg-white border border-indigo-100 rounded p-2 whitespace-pre-wrap"
             data-testid="call-assist-ask-ghost-answer">
          <div className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${labelClass(answer.source)}`}
               data-testid={`call-assist-ask-ghost-source-${answer.source}`}>
            {label(answer.source)}
          </div>
          <div className="font-semibold text-slate-900">{answer.title}</div>
          <div className="text-slate-800 mt-1">{answer.body}</div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Editor
// A single editor is used for both "new script" and "edit existing"
// modes.  When ``draft.id`` is null we treat it as create; otherwise
// update.  Rendered inline at the top of the panel so operators never
// leave the Call Outcome context.
function ScriptEditor({ draft, setDraft, categories, companies,
                        onSave, onCancel, saving, canManageGlobal,
                        supportedVars = [] }) {
  const isNew = !draft.id;
  const textareaId = `edit-body-${draft.id || 'new'}`;

  // v3.18-1342 — click a variable chip to insert ``{token}`` at the
  // current cursor position of the body textarea.  Empty ``ctx`` on
  // the composer is fine — the substitution runs at read time.
  const insertToken = (name) => {
    const ta = document.getElementById(textareaId);
    if (!ta) return;
    const start = ta.selectionStart, end = ta.selectionEnd;
    const token = `{${name}}`;
    const next  = ta.value.slice(0, start) + token + ta.value.slice(end);
    _setNativeValue(ta, next);
    ta.focus();
    const pos = start + token.length;
    ta.selectionStart = ta.selectionEnd = pos;
  };
  return (
    <div className="border border-amber-200 bg-amber-50/40 rounded p-3 mb-3"
         data-testid={isNew ? 'call-assist-new-editor' : `call-assist-editor-${draft.id}`}>
      <div className="text-xs font-semibold text-amber-800 mb-2">
        {isNew ? 'New Script' : 'Editing Script'}
      </div>

      <label className="block text-[11px] font-semibold text-slate-600 mb-0.5">Title</label>
      <input
        value={draft.title}
        onChange={(e) => setDraft(d => ({ ...d, title: e.target.value }))}
        className="w-full text-sm border border-slate-200 rounded px-2 py-1 mb-2 outline-none focus:ring-1 focus:ring-slate-400"
        placeholder="Script title"
        data-testid="call-assist-editor-title"
      />

      <div className="grid grid-cols-2 gap-2 mb-2">
        <div>
          <label className="block text-[11px] font-semibold text-slate-600 mb-0.5">Category</label>
          <select
            value={draft.category}
            onChange={(e) => setDraft(d => ({ ...d, category: e.target.value }))}
            className="w-full text-sm border border-slate-200 rounded px-2 py-1 outline-none"
            data-testid="call-assist-editor-category"
          >
            {categories.map(c => (
              <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-slate-600 mb-0.5">Scope</label>
          <select
            value={draft.scope}
            onChange={(e) => setDraft(d => ({ ...d, scope: e.target.value }))}
            className="w-full text-sm border border-slate-200 rounded px-2 py-1 outline-none"
            data-testid="call-assist-editor-scope"
          >
            <option value="global" disabled={!canManageGlobal}>Global</option>
            <option value="internal">Internal</option>
            <option value="company">Company Specific</option>
          </select>
        </div>
      </div>

      {draft.scope === 'company' && (
        <div className="mb-2">
          <label className="block text-[11px] font-semibold text-slate-600 mb-0.5">Company</label>
          <select
            value={draft.company_id || ''}
            onChange={(e) => setDraft(d => ({ ...d, company_id: e.target.value }))}
            className="w-full text-sm border border-slate-200 rounded px-2 py-1 outline-none"
            data-testid="call-assist-editor-company"
          >
            <option value="">— Select a company —</option>
            {companies.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
      )}

      <label className="block text-[11px] font-semibold text-slate-600 mb-0.5">
        Keywords <span className="text-slate-400 font-normal">(comma-separated, optional)</span>
      </label>
      <input
        value={draft.keywords}
        onChange={(e) => setDraft(d => ({ ...d, keywords: e.target.value }))}
        className="w-full text-sm border border-slate-200 rounded px-2 py-1 mb-2 outline-none"
        placeholder="e.g. intro, greeting"
        data-testid="call-assist-editor-keywords"
      />

      <label className="block text-[11px] font-semibold text-slate-600 mb-0.5">Body</label>
      <div className="flex items-center gap-1 mb-1" data-testid="call-assist-editor-toolbar">
        <RichBtn tid="fmt-bold-new"      title="Bold"        onClick={() => wrapSelection(textareaId, 'b')}><Bold size={12} /></RichBtn>
        <RichBtn tid="fmt-italic-new"    title="Italic"      onClick={() => wrapSelection(textareaId, 'i')}><Italic size={12} /></RichBtn>
        <RichBtn tid="fmt-underline-new" title="Underline"   onClick={() => wrapSelection(textareaId, 'u')}><Underline size={12} /></RichBtn>
        <RichBtn tid="fmt-ul-new"        title="Bullet list" onClick={() => wrapList(textareaId, 'ul')}><List size={12} /></RichBtn>
        <RichBtn tid="fmt-ol-new"        title="Number list" onClick={() => wrapList(textareaId, 'ol')}><ListOrdered size={12} /></RichBtn>
      </div>
      <textarea
        id={textareaId}
        value={draft.body}
        onChange={(e) => setDraft(d => ({ ...d, body: e.target.value }))}
        className="w-full text-[13px] leading-snug border border-slate-200 rounded p-2 h-32 outline-none font-mono"
        placeholder="Script body — HTML is preserved"
        data-testid="call-assist-editor-body"
      />

      {supportedVars.length > 0 && (
        <div className="mt-2" data-testid="call-assist-variables-palette">
          <div className="text-[11px] font-semibold text-slate-600 mb-1">
            Live variables — click to insert. Substituted at call time.
          </div>
          <div className="flex flex-wrap gap-1">
            {supportedVars.map(v => (
              <button
                key={v}
                type="button"
                onClick={() => insertToken(v)}
                className="inline-flex items-center h-6 px-2 text-[10px] font-mono bg-white border border-slate-200 rounded text-slate-700 hover:bg-slate-100 hover:border-slate-400"
                data-testid={`call-assist-var-${v}`}
              >
                {`{${v}}`}
              </button>
            ))}
          </div>
        </div>
      )}

      <label className="mt-2 inline-flex items-center gap-2 text-xs text-slate-700">
        <input
          type="checkbox"
          checked={!!draft.active}
          onChange={(e) => setDraft(d => ({ ...d, active: e.target.checked }))}
          data-testid="call-assist-editor-active"
        />
        Active (visible to reps)
      </label>

      <div className="flex gap-2 mt-2">
        <button
          onClick={onSave}
          disabled={saving || !draft.title.trim()}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-emerald-600 text-white rounded disabled:opacity-60"
          data-testid="call-assist-editor-save"
        >
          <Save size={12} /> {isNew ? 'Create' : 'Save'}
        </button>
        <button
          onClick={onCancel}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-white border border-slate-200 text-slate-700 rounded"
          data-testid="call-assist-editor-cancel"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Panel
export function CallAssistPanel({ open, onClose, clientId = null }) {
  const [scripts, setScripts]           = useState([]);
  const [canEdit, setCanEdit]           = useState(false);
  const [isInternal, setIsInternal]     = useState(false);
  const [categories, setCategories]     = useState(Object.keys(CATEGORY_LABELS));
  const [companies, setCompanies]       = useState([]);
  const [supportedVars, setSupportedVars] = useState([]);
  const [activeCat, setActiveCat]       = useState('opening');
  const [search, setSearch]             = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [draft, setDraft]               = useState(null);   // null => not editing
  const [saving, setSaving]             = useState(false);
  const [historyForId, setHistoryForId] = useState(null);
  const [history, setHistory]           = useState([]);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const inputRef = useRef(null);

  const canManageGlobal = canEdit;   // Only editors reach this file

  // ------------------------------------------------ Loader
  const loadScripts = useCallback(async () => {
    try {
      const params = { };
      if (clientId) params.client_id = clientId;
      if (showArchived) params.include_archived = true;
      const { data } = await api.get('/call-assist/scripts', { params });
      setScripts(data.scripts || []);
      setCanEdit(!!data.can_edit);
      setIsInternal(!!data.is_internal);
      if (data.categories?.length) setCategories(data.categories);
      // v3.18-1342 — server-published list of supported variables.
      if (Array.isArray(data.variables)) setSupportedVars(data.variables);
    } catch (e) {
      console.error('[CallAssist] load failed', e);
      toast.error('Failed to load Call Assist scripts');
    }
  }, [clientId, showArchived]);

  // ------------------------------------------------ Load on open
  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      await loadScripts();
      if (!alive) return;
      try {
        const { data } = await api.get('/call-assist/last-category');
        if (alive && data?.category) setActiveCat(data.category);
      } catch { /* silent */ }
      // Companies dropdown for the editor.  Fetched lazily; a
      // failure here shouldn't block script browsing.
      try {
        const { data } = await api.get('/companies');
        if (alive && Array.isArray(data)) {
          setCompanies(data
            .filter(c => c && c.id && c.name)
            .sort((a, b) => (a.name || '').localeCompare(b.name || '')));
        }
      } catch { /* silent */ }
      setTimeout(() => inputRef.current?.focus(), 100);
    })();
    return () => { alive = false; };
  }, [open, loadScripts]);

  // Persist last-used category as the operator switches.
  useEffect(() => {
    if (!open || !activeCat) return;
    api.put('/call-assist/last-category', { category: activeCat })
      .catch(() => { /* best-effort */ });
  }, [activeCat, open]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return scripts.filter(s => {
      if (activeCat && s.category !== activeCat && !needle) return false;
      if (!needle) return true;
      const hay = `${s.title} ${s.body} ${(s.keywords || []).join(' ')}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [scripts, search, activeCat]);

  // ------------------------------------------------ Editor lifecycle
  const startNew = () => {
    setDraft({ ...EMPTY_DRAFT, category: activeCat || 'opening' });
    setHistoryForId(null);
  };
  const startEdit = (script) => {
    setDraft({
      id:         script.id,
      title:      script.title || '',
      // v3.18-1342 — edit the RAW template with tokens, not the
      // rendered version.  ``body_raw`` is preserved by the API.
      body:       script.body_raw || script.body || '',
      category:   script.category || 'opening',
      keywords:   (script.keywords || []).join(', '),
      scope:      script.scope || 'global',
      company_id: script.company_id || '',
      active:     (script.status || 'active') === 'active',
    });
    setHistoryForId(null);
  };
  const cancelEdit = () => setDraft(null);

  const saveDraft = async () => {
    if (!draft) return;
    if (draft.scope === 'company' && !draft.company_id) {
      toast.error('Select a company for Company-Specific scripts');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title:    draft.title.trim(),
        body:     draft.body,
        category: draft.category,
        keywords: (draft.keywords || '')
                     .split(',').map(s => s.trim()).filter(Boolean),
        scope:    draft.scope,
        company_id: draft.scope === 'company' ? draft.company_id : null,
        active:   !!draft.active,
      };
      if (draft.id) {
        const { data } = await api.put(`/call-assist/scripts/${draft.id}`, payload);
        setScripts(prev => prev.map(s => s.id === draft.id ? data.script : s));
        toast.success('Script updated');
      } else {
        const { data } = await api.post('/call-assist/scripts', payload);
        // Merge in place so the newly created row appears without a
        // full reload; also refresh in case scoping hides / reveals
        // other rows (rare but possible with new company mapping).
        setScripts(prev => [...prev, data.script]);
        setActiveCat(payload.category);
        toast.success('Script created');
      }
      setDraft(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save script');
    } finally { setSaving(false); }
  };

  const doArchive = async (script) => {
    try {
      const { data } = await api.post(`/call-assist/scripts/${script.id}/archive`);
      setScripts(prev => prev.map(s => s.id === script.id ? data.script : s));
      toast.success('Script archived — restore anytime from Show Archived.');
      setConfirmDeleteId(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Archive failed');
    }
  };
  const doUnarchive = async (script) => {
    try {
      const { data } = await api.post(`/call-assist/scripts/${script.id}/unarchive`);
      setScripts(prev => prev.map(s => s.id === script.id ? data.script : s));
      toast.success('Script restored');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Restore failed');
    }
  };
  const doDuplicate = async (script) => {
    try {
      const { data } = await api.post(`/call-assist/scripts/${script.id}/duplicate`);
      setScripts(prev => [...prev, data.script]);
      toast.success('Script duplicated');
      // Open the copy in the editor so the operator can adjust it.
      startEdit(data.script);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Duplicate failed');
    }
  };

  const loadHistory = async (script) => {
    setHistoryForId(script.id);
    try {
      const { data } = await api.get(`/call-assist/scripts/${script.id}/versions`);
      setHistory(data.versions || []);
    } catch { setHistory([]); }
  };
  const restoreVersion = async (script, version) => {
    try {
      const { data } = await api.post(`/call-assist/scripts/${script.id}/restore/${version}`);
      setScripts(prev => prev.map(s => s.id === script.id ? data.script : s));
      toast.success(`Restored version ${version}`);
      setHistoryForId(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to restore version');
    }
  };

  if (!open) return null;
  // v3.21-1627 · P0 · Portal the panel to document.body AND force
  // `pointer-events-auto` on the root.
  //
  // ROOT CAUSE (the bug this fixes) — CallAssistPanel opens from
  // ClientWorkspaceModal, which wraps its content in a Radix `<Dialog>`.
  // Radix Dialog renders `<DialogOverlay>` + `<DialogContent>` through a
  // React portal at `document.body` AND simultaneously sets
  // `pointer-events: none` on `document.body` (its recommended trick to
  // guarantee outside-clicks are blocked).  Elements that inherit from
  // `<body>` and are NOT inside the Radix portal therefore visually
  // render (z-index still paints) but silently drop ALL pointer
  // events — you can see the buttons, the mouse cursor doesn't
  // change, and no click / keypress reaches React.  Every Call Assist
  // control (Search input, Ask Ghost, Ask button, category chips,
  // New Script, Edit, Duplicate, History, Delete, X, Show Archived)
  // was dead exactly because of this.
  //
  // The fix — render CallAssistPanel through OUR OWN React portal to
  // `document.body` (still visually on top thanks to z-[80]) with an
  // explicit `pointer-events-auto` so it opts out of the inherited
  // body-level lock.  We intentionally do NOT add stopPropagation
  // handlers on the wrapper — that would swallow native focus and
  // prevent inputs from receiving keyboard events.  Radix's parent
  // Client Workspace modal already uses
  // `onPointerDownOutside=preventDefault`, so it will not close when
  // the rep clicks inside Call Assist.
  const panel = (
    <div
      className="fixed top-16 bottom-6 right-4 z-[80] w-[360px] max-w-full bg-white border border-slate-200 rounded-lg shadow-2xl flex flex-col pointer-events-auto"
      data-testid="call-assist-panel"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div>
          <div className="text-sm font-semibold text-slate-900">Call Assist</div>
          <div className="text-[11px] text-slate-500">
            {canEdit
              ? 'You can create, edit, archive and restore scripts (Director / Admin).'
              : 'Read-only — Directors and Admins manage the script library.'}
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-slate-400 hover:text-slate-700"
          data-testid="call-assist-close"
        >
          <X size={18} />
        </button>
      </div>

      {/* Toolbar: search + New + archived toggle */}
      <div className="px-4 py-2 border-b border-slate-100">
        <div className="relative">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            ref={inputRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search scripts — title, body, or keyword"
            className="w-full text-sm border border-slate-200 rounded pl-7 pr-2 py-1.5 focus:ring-1 focus:ring-slate-400 outline-none"
            data-testid="call-assist-search"
          />
        </div>
        {/* v3.21-1630 · Ask Ghost inline — thin client for the
            unified Ghost service at POST /api/ghost/answer. */}
        <AskGhostInline
          clientId={clientId}
          activeCat={activeCat}
        />
        {canEdit && (
          <div className="flex items-center justify-between mt-2 gap-2">
            <button
              onClick={startNew}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-slate-900 text-white rounded hover:bg-slate-800"
              data-testid="call-assist-new"
            >
              <Plus size={12} /> New Script
            </button>
            <button
              onClick={() => setShowArchived(v => !v)}
              className={`inline-flex items-center gap-1 px-2 py-1 text-xs border rounded ${
                showArchived
                  ? 'bg-slate-200 text-slate-800 border-slate-300'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}
              data-testid="call-assist-toggle-archived"
            >
              {showArchived ? <EyeOff size={12} /> : <Eye size={12} />}
              {showArchived ? 'Hide Archived' : 'Show Archived'}
            </button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-1 px-3 py-2 border-b border-slate-100">
        {categories.map(cat => (
          <button
            key={cat}
            onClick={() => setActiveCat(cat)}
            className={`px-2 py-0.5 text-[11px] rounded-full border ${
              activeCat === cat
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
            }`}
            data-testid={`call-assist-cat-${cat}`}
          >
            {CATEGORY_LABELS[cat] || cat.replace(/_/g, ' ')}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-2 space-y-3">
        {/* Inline editor rendered above the list when active */}
        {draft && (
          <ScriptEditor
            draft={draft}
            setDraft={setDraft}
            categories={categories}
            companies={companies}
            onSave={saveDraft}
            onCancel={cancelEdit}
            saving={saving}
            canManageGlobal={canManageGlobal}
            supportedVars={supportedVars}
          />
        )}

        {/* Empty-state per category */}
        {filtered.length === 0 && !draft && (
          <div className="text-center py-8" data-testid="call-assist-empty">
            <div className="text-sm text-slate-400 mb-3">
              No scripts exist for this category.
            </div>
            {canEdit && (
              <button
                onClick={startNew}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-slate-900 text-white rounded"
                data-testid="call-assist-empty-create"
              >
                <Plus size={12} /> Create First Script
              </button>
            )}
          </div>
        )}

        {filtered.map(s => {
          const isArchived = (s.status || 'active') === 'archived';
          return (
            <div
              key={s.id}
              className={`border rounded p-3 ${
                isArchived
                  ? 'border-slate-200 bg-slate-50 opacity-80'
                  : 'border-slate-200 bg-white'
              }`}
              data-testid={`call-assist-script-${s.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-semibold text-slate-900">{s.title}</div>
                    {isArchived && (
                      <span className="text-[10px] uppercase tracking-wide bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">Archived</span>
                    )}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-2">
                    <span data-testid={`call-assist-scope-${s.id}`}>
                      {SCOPE_LABELS[s.scope] || s.scope}
                    </span>
                    <span>·</span>
                    <span>v{s.version}</span>
                  </div>
                </div>
              </div>
              <div
                className="text-[13px] leading-snug text-slate-700 mt-1 prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: s.body || '' }}
              />
              {canEdit && (
                <div className="flex flex-wrap gap-2 mt-2">
                  <button
                    onClick={() => startEdit(s)}
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] border border-slate-200 rounded text-slate-700 hover:bg-slate-50"
                    data-testid={`call-assist-edit-${s.id}`}
                  >
                    <Edit3 size={11} /> Edit
                  </button>
                  <button
                    onClick={() => doDuplicate(s)}
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] border border-slate-200 rounded text-slate-700 hover:bg-slate-50"
                    data-testid={`call-assist-duplicate-${s.id}`}
                  >
                    <Copy size={11} /> Duplicate
                  </button>
                  <button
                    onClick={() => loadHistory(s)}
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] border border-slate-200 rounded text-slate-700 hover:bg-slate-50"
                    data-testid={`call-assist-history-${s.id}`}
                  >
                    <History size={11} /> History
                  </button>
                  {isArchived ? (
                    <button
                      onClick={() => doUnarchive(s)}
                      className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] border border-emerald-200 bg-emerald-50 rounded text-emerald-700 hover:bg-emerald-100"
                      data-testid={`call-assist-unarchive-${s.id}`}
                    >
                      <ArchiveRestore size={11} /> Restore
                    </button>
                  ) : (
                    <button
                      onClick={() => setConfirmDeleteId(s.id)}
                      className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] border border-red-200 bg-red-50 rounded text-red-700 hover:bg-red-100"
                      data-testid={`call-assist-delete-${s.id}`}
                    >
                      <Trash2 size={11} /> Delete
                    </button>
                  )}
                </div>
              )}

              {/* Delete confirmation inline (per-row) */}
              {confirmDeleteId === s.id && (
                <div className="mt-2 border-t border-slate-100 pt-2 text-[12px]"
                     data-testid={`call-assist-confirm-delete-${s.id}`}>
                  <div className="text-slate-700 mb-2">
                    Delete <b>{s.title}</b>? The script will be archived and can be restored later.
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => doArchive(s)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-red-600 text-white rounded"
                      data-testid={`call-assist-confirm-delete-yes-${s.id}`}
                    >
                      <ArchiveIcon size={11} /> Archive
                    </button>
                    <button
                      onClick={() => setConfirmDeleteId(null)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-white border border-slate-200 text-slate-700 rounded"
                      data-testid={`call-assist-confirm-delete-no-${s.id}`}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {historyForId === s.id && (
                <div className="mt-2 border-t border-slate-100 pt-2 text-[12px]"
                     data-testid={`call-assist-history-panel-${s.id}`}>
                  <div className="font-semibold text-slate-600 mb-1">Version history</div>
                  {history.length === 0 && (
                    <div className="text-slate-400">No prior versions recorded.</div>
                  )}
                  {history.map(v => (
                    <div key={v.id} className="flex items-center justify-between py-0.5">
                      <span>
                        v{v.version} · {new Date(v.recorded_at).toLocaleString()}
                        {v.recorded_by?.name ? ` · ${v.recorded_by.name}` : ''}
                        {v.reason ? ` · ${v.reason}` : ''}
                      </span>
                      <button
                        onClick={() => restoreVersion(s, v.version)}
                        className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline"
                        data-testid={`call-assist-restore-${s.id}-v${v.version}`}
                      >
                        <RotateCcw size={10} /> Restore
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
  // v3.21-1627 · No createPortal — CallAssistPanel is rendered inline
  // as a JSX child of ClientWorkspaceModal's DialogContent (see
  // ClientWorkspaceModal.jsx v3.21-1627 comment).  Being an inline
  // child of DialogContent means (a) our node is inside the actual
  // DOM tree Radix's FocusScope watches, so inputs receive focus/
  // keyboard events, and (b) the pointer-events lock on <body> does
  // not apply to us because we inherit `pointer-events: auto` from
  // DialogContent (Radix explicitly sets it there).  `fixed`
  // positioning still slides the panel in from the viewport edge —
  // being a DOM child of DialogContent only affects the focus /
  // event tree, not layout.
  return panel;
}

export default CallAssistPanel;
